import React, { FC } from 'react';
import { Badge, ButtonGroup } from 'reactstrap';
import {
  HotelPublicInformation,
  HotelReservation,
  HotelReservationStatus,
} from '../../../shared/hotel';
import { ExternalLinkButton, LinkButton, MaterialIcon } from '../../components';
import { CopyButton } from '../../components/CopyButton';
import { useTranslation } from '../../translations';
import { FindMyReservation } from './FindMyReservation';
import { getReservationNights } from './util';

interface MyReservationWidgetProps {
  readonly info: HotelPublicInformation;
  readonly refresh: () => void;
}

export const MyReservationWidget: FC<MyReservationWidgetProps> = ({ info, refresh }) => {
  const { ts } = useTranslation();
  let reservation = info.reservations.find((t) => t.status === HotelReservationStatus.Confirmed);

  if (!reservation) {
    reservation = info.reservations[0];
  }

  return (
    <div>
      <div className="action-title">
        <MaterialIcon large name="hotel" />
        <span>{ts('my_reservation')}</span>
      </div>
      <div className="action-divider" />
      <div className="action-content text-small">
        {!reservation && <NoReservation refresh={refresh} />}
        {reservation &&
          (reservation.status === HotelReservationStatus.Pending ? (
            <ReservationPending refresh={refresh} reservation={reservation} />
          ) : (
            <ReservationInfo reservation={reservation} />
          ))}
      </div>
    </div>
  );
};

interface RefreshProp {
  readonly refresh: () => void;
}

interface ReservationPendingProps extends RefreshProp {
  readonly reservation: HotelReservation;
}

const ReservationPending: FC<ReservationPendingProps> = ({ reservation, refresh }) => {
  const { ts } = useTranslation();
  const { bookingUrl } = reservation;

  if (!bookingUrl) {
    return (
      <>
        <div className="text-danger text-center">{ts('common.error')}</div>
        <p>{ts('hotel.booking_link_error')}</p>
      </>
    );
  }

  return (
    <>
      <div className="text-success">
        <strong>{ts('youre_ready_to_book')}</strong>
      </div>
      <div>{ts('you_have_a_reservation_link_that_hasnt_yet_been_used')}</div>
      <div>{ts('click_the_button_below_to_be_transferred_to_the_hotel_reservation_system')}</div>
      <div className="action-content-button-list">
        <ButtonGroup className="w-100">
          <ExternalLinkButton block color="primary" href={bookingUrl} id="bookARoom">
            {ts('book_a_room')}
          </ExternalLinkButton>
          <CopyButton
            color="secondary"
            textToCopy={bookingUrl}
            toastMessage="hotel.booking_link_copied_to_clipboard"
          />
        </ButtonGroup>
        <FindMyReservation refresh={refresh} />
      </div>
    </>
  );
};

const NoReservation: FC<RefreshProp> = ({ refresh }) => {
  const { ts } = useTranslation();

  return (
    <>
      <div className="action-content-divider" />
      <p className="text-center" id="noReservations">
        {ts('you_dont_yet_have_a_reservation')}
      </p>
      <div className="action-content-divider" />
      <div className="action-content-button-list">
        <FindMyReservation refresh={refresh} />
      </div>
    </>
  );
};

interface ReservationInfoProps {
  readonly reservation: HotelReservation;
}

const ReservationInfo: FC<ReservationInfoProps> = ({ reservation }) => {
  const { guests = [] } = reservation;
  const { ts } = useTranslation();

  return (
    <>
      <div>
        <ReservationStatus reservation={reservation} />
      </div>
      <div>
        {ts('reservation')} #{reservation.extReservationId ?? reservation.id}
      </div>
      <div>
        {guests.length} {ts('guest')}
        {guests.length === 1 ? '' : 's'}
        <ReservationDates reservation={reservation} />
      </div>
      <div>{reservation.hotel?.name}</div>
      <LinkButton block to={`/hotel/reservation/${reservation.id}`}>
        {ts('manage_reservation')}
      </LinkButton>
    </>
  );
};

export const ReservationStatus: FC<ReservationInfoProps> = ({ reservation }) => {
  const { status } = reservation;
  const { ts } = useTranslation();

  switch (status) {
    case HotelReservationStatus.Creating:
    case HotelReservationStatus.Pending: {
      return null;
    }

    case HotelReservationStatus.New: {
      return <Badge color="warning">{ts('new')}</Badge>;
    }

    case HotelReservationStatus.Confirmed: {
      return <Badge color="success">{ts('confirmed')}</Badge>;
    }

    case HotelReservationStatus.Cancelled:
    case HotelReservationStatus.CancelledByEvent: {
      return <Badge color="danger">{ts('common.cancelled')}</Badge>;
    }

    case HotelReservationStatus.Expired: {
      return <Badge color="secondary">{ts('common.expired')}</Badge>;
    }

    case HotelReservationStatus.NoShow: {
      return <Badge color="danger">{ts('hotel.no_show')}</Badge>;
    }

    case HotelReservationStatus.Waitlisted: {
      return <Badge color="info">{ts('hotel.waitlisted')}</Badge>;
    }
  }
};

export const ReservationDates: FC<ReservationInfoProps> = ({ reservation }) => {
  const { ts } = useTranslation();

  const reservationStay = getReservationNights({ reservation });

  if (!reservationStay) {
    return null;
  }

  const { nights, stayDates } = reservationStay;
  const plural = nights === 1 ? null : 's';

  return (
    <>
      {' '}
      &mdash; {nights} {ts('night')}
      {plural} ({stayDates})
    </>
  );
};
